import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Eye, EyeOff, Mail } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = {
  "our-story": "Our Story",
  schedule: "Schedule",
  travel: "Travel & Hotels",
  "dress-code": "Dress Code",
  rsvp: "RSVP",
};

export default function Admin() {
  const { refresh } = useSettings();
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const [rsvps, setRsvps] = useState([]);
  const [allPages, setAllPages] = useState([]);
  const [hidden, setHidden] = useState([]);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadData = useCallback(async (tok) => {
    const cfg = { headers: { Authorization: `Bearer ${tok}` } };
    const [r, s] = await Promise.all([
      axios.get(`${API}/rsvp`, cfg),
      axios.get(`${API}/settings`),
    ]);
    setRsvps(r.data);
    setAllPages(s.data.all_pages);
    setHidden(s.data.hidden_pages);
  }, []);

  useEffect(() => {
    (async () => {
      if (!token) { setChecking(false); return; }
      try {
        await axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } });
        setAuthed(true);
        await loadData(token);
      } catch {
        localStorage.removeItem("admin_token");
        setToken("");
      } finally {
        setChecking(false);
      }
    })();
  }, [token, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
      setAuthed(true);
      await loadData(data.token);
      toast.success("Welcome back!");
    } catch {
      toast.error("Incorrect passcode");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setAuthed(false);
  };

  const togglePage = async (key) => {
    const next = hidden.includes(key) ? hidden.filter((p) => p !== key) : [...hidden, key];
    setHidden(next);
    try {
      await axios.put(`${API}/settings`, { hidden_pages: next }, auth);
      await refresh();
      toast.success("Saved");
    } catch {
      toast.error("Could not save");
      setHidden(hidden);
    }
  };

  if (checking) {
    return <div className="min-h-screen grid place-items-center text-ink font-grotesk">Loading…</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <form onSubmit={login} className="card-retro bg-cream p-8 md:p-12 w-full max-w-md" data-testid="admin-login-form">
          <Lock className="text-orange" />
          <h1 className="font-display text-4xl text-ink mt-4">Private Area</h1>
          <p className="font-body text-ink/70 mt-2">Enter the shared passcode to view RSVPs.</p>
          <input
            type="password"
            className="input-retro mt-6"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            data-testid="admin-passcode-input"
          />
          <button type="submit" disabled={loading} className="btn-retro orange w-full justify-center mt-5 disabled:opacity-60" data-testid="admin-login-button">
            {loading ? "Checking…" : "Unlock →"}
          </button>
        </form>
      </div>
    );
  }

  const attending = rsvps.filter((r) => r.attending === "yes");
  const totalGuests = attending.reduce((n, r) => n + (r.guests || 0), 0);

  return (
    <div className="min-h-screen pt-28 md:pt-36 pb-24 max-w-7xl mx-auto px-6 md:px-8">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-orange">Private Dashboard</p>
          <h1 className="font-display text-5xl md:text-6xl text-ink mt-3">RSVP Inbox</h1>
        </div>
        <button onClick={logout} className="btn-retro" data-testid="admin-logout-button">
          <LogOut size={18} /> Lock
        </button>
      </div>

      {/* stats */}
      <div className="grid sm:grid-cols-3 gap-5 mt-10">
        {[
          { k: "Replies", v: rsvps.length, bg: "bg-blueglass" },
          { k: "Attending", v: attending.length, bg: "bg-cottoncandy" },
          { k: "Total guests", v: totalGuests, bg: "bg-butter" },
        ].map((s) => (
          <div key={s.k} className={`card-retro ${s.bg} p-6`}>
            <p className="overline text-ink/70">{s.k}</p>
            <p className="font-display text-5xl text-ink mt-2">{s.v}</p>
          </div>
        ))}
      </div>

      {/* page visibility toggles */}
      <div className="card-retro bg-cream p-8 mt-12" data-testid="admin-visibility-panel">
        <h2 className="font-grotesk font-extrabold text-2xl text-ink tracking-tight">Page visibility</h2>
        <p className="font-body text-ink/70 mt-1">Hide any page from the guest menu until it's ready.</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mt-6">
          {allPages.map((key) => {
            const isHidden = hidden.includes(key);
            return (
              <button
                key={key}
                onClick={() => togglePage(key)}
                data-testid={`toggle-${key}`}
                className={`flex items-center justify-between gap-3 py-3 px-4 rounded-xl border-2 border-ink font-grotesk font-bold transition-transform duration-200 hover:-translate-y-0.5 ${
                  isHidden ? "bg-cream text-ink/50" : "bg-yellow text-ink"
                }`}
              >
                {LABELS[key]}
                {isHidden ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* rsvp list */}
      <div className="mt-12 space-y-4" data-testid="admin-rsvp-list">
        {rsvps.length === 0 && <p className="font-body text-ink/60">No replies yet.</p>}
        {rsvps.map((r) => (
          <div key={r.id} className="card-retro bg-cream p-6" data-testid="admin-rsvp-item">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="font-grotesk font-extrabold text-xl text-ink">{r.name}</p>
                <p className="font-body text-ink/70 flex items-center gap-2 mt-1">
                  <Mail size={15} /> {r.email}
                </p>
              </div>
              <span
                className={`font-grotesk font-bold text-sm px-3 py-1 rounded-full border-2 border-ink ${
                  r.attending === "yes" ? "bg-avocado text-cream" : "bg-red text-cream"
                }`}
              >
                {r.attending === "yes" ? `Attending · ${r.guests}` : "Declined"}
              </span>
            </div>
            {(r.meal || r.song || r.message) && (
              <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm font-body text-ink/80">
                {r.meal && <p><span className="text-ink/50">Meal:</span> {r.meal}</p>}
                {r.song && <p><span className="text-ink/50">Song:</span> {r.song}</p>}
                {r.message && <p className="sm:col-span-3"><span className="text-ink/50">Note:</span> {r.message}</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
